import React from 'react';
import { CheckCircle, ArrowRight, Phone } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services } from '../data/mock';
import { Link } from 'react-router-dom';

const Services = () => {
  const getIcon = (iconName) => {
    const iconMap = {
      'home': LucideIcons.Home,
      'chef-hat': LucideIcons.ChefHat,
      'zap': LucideIcons.Zap,
      'droplet': LucideIcons.Droplet,
      'clock': LucideIcons.Clock,
      'paintbrush': LucideIcons.Paintbrush
    };
    const Icon = iconMap[iconName] || LucideIcons.Circle;
    return <Icon size={32} />;
  };

  // Helper function to get service image URL (replaces nested ternaries)
  const getServiceImageUrl = (serviceId) => {
    const imageMap = {
      1: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/slh1yjh7_2ee77800-476c-4ae8-a9da-716623f83482.png',
      2: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=600&h=400&fit=crop&q=80',
      3: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/wekk1usa_hrqzrzqrhq.png',
      4: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/gvsqrt02_01-tarif-plombier-prix-travaux-plomberie.jpg',
      5: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/l56caivu_e5f283f4-5c55-4b60-ba3f-35a038fb1523.png',
      6: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/rq5c31v9_depannage-urgence.jpg'
    };
    return imageMap[serviceId] || imageMap[6];
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Page Header */}
      <section className="text-white py-20 mb-16" style={{
        background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
      }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}>
            Nos <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Services</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            De la conception à la réalisation, nous maîtrisons tous les aspects de la construction et de la rénovation pour vous offrir un service complet et de qualité.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
            >
              {/* Image en haut */}
              <div className="relative w-full h-64 overflow-hidden">
                <img 
                  src={getServiceImageUrl(service.id)}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                
                {/* Badge doré avec icône en coin supérieur */}
                <div className="absolute top-4 right-4 w-14 h-14 bg-brand-gold rounded-xl flex items-center justify-center text-white shadow-xl">
                  {getIcon(service.icon)}
                </div>
              </div>

              <div className="p-8 md:p-10">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-brand-gold transition-colors">
                    {service.title}
                  </h2>
                  <div className="w-16 h-1 bg-brand-blue"></div>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                  {service.description}
                </p>

                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-800 mb-4">Nos prestations incluent :</h3>
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-brand-blue mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="container mx-auto px-4 mt-20">
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 md:p-12" style={{
          border: '4px solid transparent',
          backgroundImage: 'linear-gradient(to bottom right, rgb(249, 250, 251), white), linear-gradient(to right, #f9a01b, #ffd230)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box'
        }}>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: '#0066CC' }}>
            Notre <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Processus</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-gold text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-bold text-lg text-gray-800 mb-2">Contact</h3>
              <p className="text-gray-600 text-sm">
                Prenez contact avec nous par téléphone ou formulaire
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-gold text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-bold text-lg text-gray-800 mb-2">Visite & Devis</h3>
              <p className="text-gray-600 text-sm">
                Visite sur place et établissement d'un devis détaillé gratuit
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-gold text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-bold text-lg text-gray-800 mb-2">Planification</h3>
              <p className="text-gray-600 text-sm">
                Planification détaillée et coordination des travaux
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-gold text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-bold text-lg text-gray-800 mb-2">Réalisation</h3>
              <p className="text-gray-600 text-sm">
                Exécution des travaux avec suivi régulier et qualité
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 mt-20">
        <div className="rounded-2xl shadow-2xl p-8 md:p-12 text-center" style={{
          background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
        }}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
            Discutons de <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Votre Projet</span>
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto mt-6">
            Chaque projet est unique. Contactez-nous pour un accompagnement personnalisé et un devis sur mesure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
            >
              Demander mon devis gratuit
              <ArrowRight size={20} />
            </Link>
            <a 
              href="tel:0471664418"
              className="inline-flex items-center justify-center gap-2 bg-white text-brand-hero px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-gold hover:text-gray-900 transition-all hover:shadow-xl"
            >
              <Phone size={20} />
              Appelez-nous
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
